import { memo, useState } from "react";

import LoadingButton from "@mui/lab/LoadingButton";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Select from "@mui/material/Select";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import { LoginMethod } from "stores/lib";
import { useOrganizationStore } from "stores/OrganizationStore";

import asyncOrSwim from "utility/async_or_swim";
import { authenticatedGetFetch } from "utility/authenticated_fetch";
import { notifyError, notifySuccess } from "utility/notify";
import throwIf from "utility/throw_if";

import ADFSSAMLSSOIcon from "layouts/components/Icon/SSO/ADFSSAML";
import ADPOIDCSSOIcon from "layouts/components/Icon/SSO/ADPOIDC";
import Auth0SAMLSSOIcon from "layouts/components/Icon/SSO/Auth0SAML";
import AzureSAMLSSOIcon from "layouts/components/Icon/SSO/AzureSAML";
import CasSAMLSSOIcon from "layouts/components/Icon/SSO/CasSAML";
import ClassLinkSAMLSSOIcon from "layouts/components/Icon/SSO/ClassLinkSAML";
import CloudflareSAMLSSOIcon from "layouts/components/Icon/SSO/CloudflareSAML";
import CyberArkSAMLSSOIcon from "layouts/components/Icon/SSO/CyberArkSAML";
import DuoSAMLSSOICon from "layouts/components/Icon/SSO/DuoSAML";
import GenericOIDCSSOIcon from "layouts/components/Icon/SSO/GenericOIDC";
import GenericSAMLSSOIcon from "layouts/components/Icon/SSO/GenericSAML";
import GoogleSAMLSSOIcon from "layouts/components/Icon/SSO/GoogleSAML";
import JumpCloudSAMLSSOIcon from "layouts/components/Icon/SSO/JumpCloudSAML";
import KeycloakSAMLSSOIcon from "layouts/components/Icon/SSO/KeycloakSAML";
import MiniOrangeSAMLSSOIcon from "layouts/components/Icon/SSO/MiniOrangeSAML";
import NetIQSAMLSSOIcon from "layouts/components/Icon/SSO/NetIQSAML";
import OktaSAMLSSOIcon from "layouts/components/Icon/SSO/OktaSAML";
import OneLoginSAMLSSOIcon from "layouts/components/Icon/SSO/OneLoginSAML";
import OracleSAMLSSOIcon from "layouts/components/Icon/SSO/OracleSAML";
import PingFederateSAMLSSOIcon from "layouts/components/Icon/SSO/PingFederateSAML";
import PingOneSAMLSSOIcon from "layouts/components/Icon/SSO/PingOneSAML";
import SalesforceSAMLSSOIcon from "layouts/components/Icon/SSO/SalesforceSAML";
import SimpleSamlPhpSAMLSSOIcon from "layouts/components/Icon/SSO/SimpleSamlPhpSAML";
import VMwareSAMLSSOIcon from "layouts/components/Icon/SSO/VMwareSAML";

// -----------------------------------------------------------------------------

const ConnectionIcon: Record<string, Function> = {
  // GoogleOAuth: no longer an option
  // LastPassSAML: no longer an option
  // MagicLink: no icon
  // MicrosoftOAuth: no longer an option
  // ShibbolethSAML: no longer an option

  ADFSSAML: ADFSSAMLSSOIcon,
  ADPOIDC: ADPOIDCSSOIcon,
  Auth0SAML: Auth0SAMLSSOIcon,
  AzureSAML: AzureSAMLSSOIcon,
  CASSAML: CasSAMLSSOIcon,
  ClassLinkSAML: ClassLinkSAMLSSOIcon,
  CloudflareSAML: CloudflareSAMLSSOIcon,
  CyberArkSAML: CyberArkSAMLSSOIcon,
  DuoSAML: DuoSAMLSSOICon,
  GenericOIDC: GenericOIDCSSOIcon,
  GenericSAML: GenericSAMLSSOIcon,
  GoogleSAML: GoogleSAMLSSOIcon,
  JumpCloudSAML: JumpCloudSAMLSSOIcon,
  KeycloakSAML: KeycloakSAMLSSOIcon,
  MiniOrangeSAML: MiniOrangeSAMLSSOIcon,
  NetIqSAML: NetIQSAMLSSOIcon,
  OktaSAML: OktaSAMLSSOIcon,
  OneLoginSAML: OneLoginSAMLSSOIcon,
  OracleSAML: OracleSAMLSSOIcon,
  PingFederateSAML: PingFederateSAMLSSOIcon,
  PingOneSAML: PingOneSAMLSSOIcon,
  SalesforceSAML: SalesforceSAMLSSOIcon,
  SimpleSamlPhpSAML: SimpleSamlPhpSAMLSSOIcon,
  VMwareSAML: VMwareSAMLSSOIcon,
};

// -----------------------------------------------------------------------------

export const OrganizationSettingsGeneralSSO = memo(
  function OrganizationSettingsGeneralSSO({ disabled }: Props): JSX.Element {
    const [organizationStore, organizationActions] = useOrganizationStore();

    //
    const [isLoading, setIsLoading] = useState(false);
    const [open, setOpen] = useState(false);
    const [loginMethod, setLoginMethod] = useState("");
    const [loginMethodError, setLoginMethodError] = useState(false);

    //
    function redirectToWorkOSUrl() {
      asyncOrSwim(
        async () => {
          setIsLoading(true);
          const requestPayload = await authenticatedGetFetch(
            `api/organization/sso_admin`
          );
          throwIf(
            requestPayload.error,
            requestPayload.message ?? "There was an error"
          );

          //
          // nosemgrep: javascript.browser.security.open-redirect.js-open-redirect
          window.location.href = requestPayload.admin_portal_url;
        },
        (error) => {
          console.error(error);
          setIsLoading(false);
          notifyError("There was an error configuring SSO");
        }
      );
    }

    const openDialog = () => {
      setOpen(true);
    };

    const closeDialog = () => {
      setOpen(false);
      setLoginMethod("");
      setLoginMethodError(false);
    };

    const getLoginMethodOptions = () => {
      const options: any = [];
      Object.keys(organizationStore.organization.workos_conn_id_dict ?? {}).map(
        (method) => {
          return options.push(
            <MenuItem value={method} key={method}>
              {LoginMethod[method]}
            </MenuItem>
          );
        }
      );
      return options;
    };

    const saveLoginMethod = () => {
      if (!loginMethod) {
        setLoginMethodError(true);
        return;
      }

      organizationActions.bulkUpdateLoginMethod({
        data: { login_method: loginMethod },
        onSuccess: onLoginMethodSuccess,
        onError: onLoginMethodError,
      });
    };

    const onLoginMethodSuccess = () => {
      closeDialog();
      notifySuccess("Login method successfully updated");
    };

    const onLoginMethodError = () => {
      closeDialog();
      notifyError("There was an error updating the login method");
    };

    //
    const isConfirmed = !!organizationStore.organization.dcv_isvalid;
    const isSSOEnabled = true;
    const connections = Object.keys(
      (organizationStore.organization.workos_conn_id_dict ??
        []) as unknown as Record<string, string>
    );

    //
    return (
      <>
        <Paper
          elevation={1}
          sx={
            isConfirmed && !disabled
              ? SSOSectionAvailableSX
              : SSOSectionUnavailableSX
          }
        >
          <Typography variant="h6" sx={TextSX} fontWeight="bold">
            Single Sign On (SSO)
          </Typography>

          <Typography variant="body1" sx={TextSX}>
            Our SSO solution is easy to set up and use, and it&apos;s compatible
            with a wide range of software applications. Configure your
            company&apos;s specific SSO provider below. Domain Control
            Validation (DCV) must be enabled first.
          </Typography>

          {isSSOEnabled ? (
            <>
              <Typography
                variant="body1"
                fontWeight="bold"
                sx={ConnectedTextSX}
              >
                Configured SSO Providers:
              </Typography>
              <Box sx={ConnectedServicesSX}>
                {connections.length > 0 ? (
                  connections.map((connection) => {
                    const Icon = ConnectionIcon[connection];

                    //
                    return (
                      <Box sx={ConnectedServiceSX} key={connection}>
                        {Icon ? (
                          <Icon sx={ConnectedServiceIconSX} />
                        ) : (
                          <Box sx={ConnectedServiceIconSX}>{connection}</Box>
                        )}
                      </Box>
                    );
                  })
                ) : (
                  <Typography variant="body1">
                    No services configured.
                  </Typography>
                )}
              </Box>
            </>
          ) : null}

          <Stack direction="row" spacing={2}>
            <LoadingButton
              variant={isSSOEnabled ? "outlined" : "contained"}
              loading={isLoading}
              onClick={redirectToWorkOSUrl}
              disabled={!isConfirmed || disabled}
            >
              {isSSOEnabled ? "Edit SSO Configuration" : "Enable SSO"}
            </LoadingButton>
            <Button
              variant="outlined"
              onClick={openDialog}
              disabled={!isConfirmed || disabled}
            >
              Update organization login method
            </Button>
          </Stack>
        </Paper>

        <Dialog open={open} onClose={closeDialog} fullWidth>
          <DialogTitle>Update Organization Login Method</DialogTitle>

          <DialogContent>
            <Stack spacing={2}>
              <DialogContentText>
                Update the login method for all members currently in the
                organization.
              </DialogContentText>

              <FormControl required fullWidth error={loginMethodError}>
                <InputLabel id="select-login-label">Login Method</InputLabel>
                <Select
                  labelId="select-login-label"
                  id="select-login"
                  value={loginMethod}
                  label="Login Method"
                  onChange={(event) => {
                    setLoginMethod(event.target.value);
                  }}
                >
                  {getLoginMethodOptions()}
                </Select>
                {loginMethodError ? (
                  <FormHelperText>Required</FormHelperText>
                ) : null}
              </FormControl>
            </Stack>
          </DialogContent>

          <DialogActions>
            <Button onClick={closeDialog}>Cancel</Button>
            <Button
              variant="contained"
              onClick={saveLoginMethod}
              disabled={!isConfirmed}
            >
              Save
            </Button>
          </DialogActions>
        </Dialog>
      </>
    );
  }
);

// -----------------------------------------------------------------------------

const SSOSectionAvailableSX = {
  mt: 2,
  p: 2,
};

const SSOSectionUnavailableSX = {
  ...SSOSectionAvailableSX,
  opacity: 0.5,
  pointerEvents: "none",
};

const TextSX = { mb: 1 };

const ConnectedTextSX = { mt: 1, mb: 1 };

const ConnectedServicesSX = {
  display: "flex",
  flexDirection: "row",
  flexWrap: "wrap",
  mb: 2,
  ml: -2,
  mt: -2,
};

const ConnectedServiceSX = {
  alignItems: "center",
  border: "1px solid rgba(0, 0, 0, 0.23)",
  borderRadius: 1,
  display: "flex",
  flex: "0 0",
  height: "60px",
  ml: 2,
  mt: 2,
  padding: 1,
  width: "120px",
};

const ConnectedServiceIconSX = {
  height: "20px",
  textAlign: "center",
  textOverflow: "ellipsis",
  width: "100px",
};

// -----------------------------------------------------------------------------

interface Props {
  disabled: boolean;
}
